<template>
  <Loader :is-loading="isLoading" :loading-message="$t('Loading layout...')"/>
  <keep-alive>
    <div v-if="initialized" style="height: 100%;" class="content-wrapper">
      <Toast position="top-center"/>
      <Toast group="bottom" position="bottom-center"/>
      <ConfirmDialog></ConfirmDialog>
      <component :is="layout"></component>
    </div>
  </keep-alive>
</template>

<script>
import user from '../mixins/user';
import {mapGetters} from "vuex";
import {config} from "@/config/config";
import localforage from 'localforage';
import {Dialog} from '@capacitor/dialog';
import {v4 as uuid} from "uuid";
import {defineAsyncComponent} from 'vue';
import { clear } from "idb-keyval";

export default {
  name: 'AppWrapper',
  components: {
    Loader: defineAsyncComponent(() => import('@/components/common/Loader.vue')),
    SyncMapMobileUnauthorized: defineAsyncComponent(() => import('@/layout/syncmap/Unauthorized.vue')),
    SyncMapMobileApp: defineAsyncComponent(() => import('@/layout/syncmap/App.vue')),
    MobileApp: defineAsyncComponent(() => import('@/layout/mobile/App.vue')),
    MobileUnauthorized: defineAsyncComponent(() => import('@/layout/mobile/Unauthorized.vue')),
    SyncDocApp: defineAsyncComponent(() => import('@/layout/syncdoc/App.vue')),
    SyncDocUnauthorized: defineAsyncComponent(() => import('@/layout/syncdoc/Unauthorized.vue')),
    ErrorLayout: defineAsyncComponent(() => import('@/layout/Error.vue')),
    CitizenApp: defineAsyncComponent(() => import('@/layout/citizen/App.vue')),
    CitizenUnauthorized: defineAsyncComponent(() => import('@/layout/citizen/Unauthorized.vue')),
    PublicApp: defineAsyncComponent(() => import('@/layout/public/App.vue')),
    NotFound: defineAsyncComponent(() => import('./NotFound.vue')),
    App: defineAsyncComponent(() => import('./App.vue')),
    Unauthorized: defineAsyncComponent(() => import('./Unauthorized.vue')),
    LoginLayout: defineAsyncComponent(() => import('./LoginLayout.vue')),
    RecoverPasswordLayout: defineAsyncComponent(() => import('./RecoverPasswordLayout.vue')),
    ResetPasswordLayout: defineAsyncComponent(() => import('./ResetPasswordLayout.vue')),
    ServerError: defineAsyncComponent(() => import('@/layout/ServerError.vue')),
    BoardUnauthorized: defineAsyncComponent(() => import('@/layout/board/Unauthorized.vue')),
    BoardApp: defineAsyncComponent(() => import('@/layout/board/App.vue')),
    UrbanPlannerUnauthorized: defineAsyncComponent(() => import('@/layout/urban-planner/Unauthorized.vue')),
    UrbanPlannerApp: defineAsyncComponent(() => import('@/layout/urban-planner/App.vue')),
    PublicAppLayout: defineAsyncComponent(() => import('@/layout/public-app/App.vue')),
    PublicAppUnauthorized: defineAsyncComponent(() => import('@/layout/public-app/Unauthorized.vue')),
    Empty: defineAsyncComponent(() => import('@/layout/Empty.vue')),
  },
  mixins: [user],
  computed: {
    ...mapGetters({
      mapSettings: 'GET_MAP_SETTINGS',
      iframe: 'GET_IFRAME',
      common: 'GET_COMMON_DATA',
      token: 'GET_TOKEN'
    }),
    isAuthenticated() {
      return !!((this.user && this.token) || this.iframe);
    },
    layout() {
      let layoutComponent;
      if(this.isAuthenticated || this.$route.name === 'Embed'){
        if(this.appType === 'default'){
          if(this.appName === 'SyncDoc'){
            layoutComponent = 'SyncDocApp';
          }else{
            layoutComponent = 'App';
          }
        } else if(this.appType === 'citizen'){
          layoutComponent = 'CitizenApp';
        } else if(this.appType === 'public'){
          layoutComponent = 'PublicApp';
        } else if(this.appType === 'urban-planner'){
          layoutComponent = 'UrbanPlannerApp';
        } else if(this.appType === 'board'){
          layoutComponent = 'BoardApp';
        } else if(this.appType === 'mobile'){
          layoutComponent = 'MobileApp';
        } else if(this.appType === 'syncmap'){
          layoutComponent = 'SyncMapMobileApp';
        } else if(this.appType === 'public-app'){
          layoutComponent = 'PublicAppLayout';
        }
      }else{
        if(this.appType === 'default'){
          if(this.appName === 'SyncDoc'){
            layoutComponent = 'SyncDocUnauthorized';
          }else{
            layoutComponent = 'Unauthorized';
          }
        } else if(this.appType === 'citizen'){
          layoutComponent = 'CitizenUnauthorized';
        } else if(this.appType === 'urban-planner'){
          layoutComponent = 'UrbanPlannerUnauthorized';
        } else if(this.appType === 'board'){
          layoutComponent = 'BoardUnauthorized';
        } else if(this.appType === 'mobile'){
          layoutComponent = 'MobileUnauthorized';
        } else if(this.appType === 'syncmap'){
          layoutComponent = 'SyncMapMobileUnauthorized';
        } else if(this.appType === 'public-app'){
          layoutComponent = 'PublicAppUnauthorized';
        }
      }

      if (this.$route.meta.layout === 'ServerError') {
        layoutComponent = 'ServerError';
      } else if (this.$route.meta.layout === 'Error') {
        layoutComponent = 'Error';
      } else if (this.$route.meta.layout === 'NotFound') {
        layoutComponent = 'NotFound';
      } else if (this.$route.meta.layout === 'Empty') {
        layoutComponent = 'Empty';
      } else if (this.$route.meta.layout === 'LoginLayout') {
        layoutComponent = 'LoginLayout';
      } else if (this.$route.meta.layout === 'RecoverPasswordLayout') {
        layoutComponent = 'RecoverPasswordLayout';
      } else if (this.$route.meta.layout === 'ResetPasswordLayout') {
        layoutComponent = 'ResetPasswordLayout';
      }

      return layoutComponent;
    },
    appType() {
      return config.APP_TYPE;
    },
    appName() {
      return config.APP_NAME;
    },
    defaultPageTitle() {
      return config.APP_NAME + ' ' + config.APP_VERSION;
    },
  },
  created() {
    if (this.appType) {
      import(`@/assets/${this.appType}/app.scss`);
    }
    this.routes = this.$router?.getRoutes();
    this.setLanguage();

    window.addEventListener('resize', this.onWindowResize);

    localforage.getItem('vuex').then((_) => {
      this.initialized = true;
      this.isLoading = false;
      this.init();
    });

    if(this.$route.meta.requireLogin && !this.isAuthenticated){
      clear().then(() => {
        this.$store.dispatch("signOut").then(() => {
          this.$router.push('/login');
        });
      });
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        this.$store.dispatch('setCurrentForms', {
          documentIds: {},
          documentTypeIds: {},
        });
        document.title = to?.meta?.breadcrumb?.length
          ? this.$t(to.meta.breadcrumb[0].label) + ' - ' + this.defaultPageTitle
          : this.defaultPageTitle;
        if (this.user || this.iframe) {
          document.querySelector('html')?.classList?.add('logged-in');
        } else {
          document.querySelector('html')?.classList?.remove('logged-in');
        }
      },
    },
    '$root.$i18n.locale': {
      immediate: true,
      handler(to, from) {
        sessionStorage.setItem("lang", to);
        window.dispatchEvent(new Event('langChanged'));
      },
    }
  },
  data() {
    return {
      isLoading: true,
      routes: [],
      initialized: false,
    };
  },
  methods: {
    onWindowResize() {
      this.$nextTick(() => {
        this.$windowWidth = window.innerWidth;
      });
    },
    init() {
      if (this.appType) {
        document.querySelector('html').classList.add(this.appType);
        document.querySelector('body').classList.add(this.appType);
      }

      if (config.APP_COLOR_THEME) {
        document.querySelector('html').classList.add(config.APP_COLOR_THEME);
      }

      if (config.THEME_OVERWRITE) {
        document.querySelector('html').classList.add('overridden');
        document.querySelector('html').classList.add(config.THEME_OVERWRITE);
      }

      this.toggleDarkMode(this.mapSettings?.darkTheme);
    },

    async showAppModeChange(status) {
      const {value} = await Dialog.confirm({
        title: 'Confirm',
        message: status.connected ? `You are now online. Switch to online mode?` : `You are now offline. Switch to offline mode?`,
      });

      if (value === true) {
        this.setAppMode(status.connected ? 'default' : 'offline');
      }
    },

    setAppMode(mode) {
      localStorage.setItem('appMode', mode);
      if (mode === 'offline') {
        this.$store.dispatch('setUser', {
          account: 'offline',
          userId: uuid()
        })
        this.$store.commit('SET_TOKEN', uuid())
      }
      location.reload();
    },
    toggleDarkMode(dark) {
      if (this.iframe) return;

      const html = document.querySelector('html');
      if (!dark) {
        html.classList.remove('dark');
        html.classList.add('light');
      } else {
        html.classList.add('dark');
        html.classList.remove('light');
      }
    },
    setLanguage() {
      this.$root.$i18n.locale = this.mapSettings?.language || 'ro';
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/app.scss';

.vld-overlay.no-opacity .vld-background {
  opacity: 1 !important;
}
</style>
